import React, { useState, useEffect, useMemo } from 'react';
import {
    Modal,
    Button,
    Box,
    Text,
    Alert,
    Accordion,
    Badge,
    Divider,
    Table,
    Paper,
    ActionIcon,
    Tooltip,
    Group,
    ScrollArea,
} from '@mantine/core';
import { LoadingState } from '../common/LoadingState';
import {
    IconCircleCheck,
    IconAlertCircle,
    IconX,
    IconCopy,
    IconTool,
    IconClock,
    IconDatabase,
    IconFilter
} from '@tabler/icons-react';
import { MODAL_SIZES, TABLE_SCROLL_MIN_WIDTH } from '../../theme/mantineTheme';
import { SetCompilationService, SetCompilationResult, SetCompilationInfo } from '../../services/SetCompilationService';
import { EntityDetailDialog } from './EntityDetailDialog';
import { DialogErrorAlert } from './DialogErrorAlert';
import { entityService } from '../../services/entityServiceSingleton';
import { copyToClipboard } from '../../utils/clipboardUtils';
import { formatDateTime } from '../../utils/dateTimeUtils';

interface SetCompilationDialogProps {
    open: boolean;
    onClose: () => void;
}

export const SetCompilationDialog: React.FC<SetCompilationDialogProps> = ({
    open,
    onClose
}) => {
    const [result, setResult] = useState<SetCompilationResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedSet, setSelectedSet] = useState<SetCompilationInfo | null>(null);
    const [selectedSetEntity, setSelectedSetEntity] = useState<any | null>(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const compilationService = useMemo(() => new SetCompilationService(), []);

    useEffect(() => {
        if (open) {
            loadCompilations();
        }
    }, [open]);

    const loadCompilations = async () => {
        setLoading(true);
        setError(null);
        setResult(null);

        try {
            const compilationResult = await compilationService.getAllSetCompilations();
            setResult(compilationResult);
        } catch (err) {
            console.error('Error in SetCompilationDialog:', err);
            setError(err instanceof Error ? err.message : 'Unknown error occurred');
        } finally {
            setLoading(false);
        }
    };

    const handleRetry = () => {
        loadCompilations();
    };

    const handleCopyResult = () => {
        if (result?.data) {
            copyToClipboard(JSON.stringify(result.data, null, 2));
        }
    };

    const getStatusBadge = (set: SetCompilationInfo) => {
        if (set.HasError) {
            return <Badge leftSection={<IconAlertCircle size={12} />} color="red" size="sm">Error</Badge>;
        }
        if (set.HasCompiled) {
            return <Badge leftSection={<IconCircleCheck size={12} />} color="green" size="sm">Compiled</Badge>;
        }
        return <Badge leftSection={<IconClock size={12} />} color="yellow" size="sm">Pending</Badge>;
    };

    const getSetTypeBadge = (setType: string) => {
        const color = setType === 'manual' ? 'blue' : setType === 'enumerated' ? 'gray' : 'default';
        return <Badge variant="outline" color={color} size="sm">{setType}</Badge>;
    };

    const renderSetDetails = (set: SetCompilationInfo) => (
        <Accordion key={set.SetId}>
            <Accordion.Item value={set.SetId}>
                <Accordion.Control>
                    <Group gap="xs">
                        <Text size="sm" fw={500}>
                            {set.SetDisplayName}
                        </Text>
                        {getStatusBadge(set)}
                        {getSetTypeBadge(set.SetType)}
                    </Group>
                </Accordion.Control>
                <Accordion.Panel>
                    <Box mb="md">
                        <Text size="sm" c="dimmed" component="div" mb="xs">
                            <strong>Set ID:</strong> {set.SetId}
                        </Text>
                        <Text size="sm" c="dimmed" component="div" mb="xs">
                            <strong>Last Compiled:</strong> {set.LastCompiled ? formatDateTime(set.LastCompiled) : 'Never'}
                        </Text>
                        <Text size="sm" c="dimmed" component="div" mb="xs">
                            <strong>Manual Members:</strong> {set.ManualMemberCount}
                        </Text>
                        {set.ScopedEntityTypes && set.ScopedEntityTypes.length > 0 && (
                            <Text size="sm" c="dimmed" component="div" mb="xs">
                                <strong>Scoped Entity Types:</strong> {set.ScopedEntityTypes.join(', ')}
                            </Text>
                        )}
                        {set.Filter && (
                            <Text size="sm" c="dimmed" component="div" mb="xs">
                                <strong>Filter:</strong> {set.Filter}
                            </Text>
                        )}
                        <DialogErrorAlert error={set.Error ?? null} />
                    </Box>

                    {set.SqlStatement && (
                        <Accordion>
                            <Accordion.Item value={`${set.SetId}-sql`}>
                                <Accordion.Control>
                                    <Group gap="xs">
                                        <IconDatabase size={16} />
                                        <Text size="sm" fw={500}>SQL Statement</Text>
                                    </Group>
                                </Accordion.Control>
                                <Accordion.Panel>
                                    <Paper withBorder p="md" mah={300} style={{ overflow: 'auto' }}>
                                        <Text component="pre" size="sm" ff="monospace" m={0}>
                                            {set.SqlStatement}
                                        </Text>
                                    </Paper>
                                </Accordion.Panel>
                            </Accordion.Item>
                        </Accordion>
                    )}

                    {set.SqlParameterStatement && (
                        <Accordion>
                            <Accordion.Item value={`${set.SetId}-params`}>
                                <Accordion.Control>
                                    <Group gap="xs">
                                        <IconFilter size={16} />
                                        <Text size="sm" fw={500}>SQL Parameters</Text>
                                    </Group>
                                </Accordion.Control>
                                <Accordion.Panel>
                                    <Paper withBorder p="md" mah={200} style={{ overflow: 'auto' }}>
                                        <Text component="pre" size="sm" ff="monospace" m={0}>
                                            {set.SqlParameterStatement}
                                        </Text>
                                    </Paper>
                                </Accordion.Panel>
                            </Accordion.Item>
                        </Accordion>
                    )}
                </Accordion.Panel>
            </Accordion.Item>
        </Accordion>
    );

    return (
        <Modal
            opened={open}
            onClose={onClose}
            size={MODAL_SIZES.detail}
            styles={{ body: { minHeight: '80vh' } }}
            title={
                <Tooltip label="SetCompilationDialog" withArrow position="top">
                    <span style={{ cursor: 'help' }}>
                        <Group justify="space-between" flex={1} w="100%" component="span">
                            <Group gap="xs" component="span">
                                <IconTool size={20} />
                                <Text fw={600} component="span">Set Compilation Results</Text>
                            </Group>
                            <Button
                        onClick={onClose}
                        size="xs"
                        variant="subtle"
                        leftSection={<IconX size={16} />}
                    >
                        Close
                    </Button>
                        </Group>
                    </span>
                </Tooltip>
            }
        >
            {loading && (
                <LoadingState variant="centered" label="Compiling sets..." size={40} />
            )}

            {error && (
                <Alert color="red" mb="md">
                    <Group justify="space-between" wrap="nowrap">
                        <Text size="sm"><strong>Error:</strong> {error}</Text>
                        <Button variant="subtle" size="xs" color="white" onClick={handleRetry}>
                            Retry
                        </Button>
                    </Group>
                </Alert>
            )}

            {result && result.success && result.data && (
                <Box>
                    <Group justify="space-between" align="center" mb="md">
                        <Alert color="green" flex={1}>
                            <Text size="sm">
                                <strong>Success!</strong> {result.message}
                            </Text>
                        </Alert>
                        <Tooltip label="Copy all results as JSON">
                            <ActionIcon onClick={handleCopyResult} size="sm" variant="subtle">
                                <IconCopy size={16} />
                            </ActionIcon>
                        </Tooltip>
                    </Group>

                    <ScrollArea>
                        <Paper withBorder mb="md">
                            <Table.ScrollContainer minWidth={TABLE_SCROLL_MIN_WIDTH}>
                                <Table highlightOnHover stickyHeader>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th><strong>Set Name</strong></Table.Th>
                                            <Table.Th><strong>Status</strong></Table.Th>
                                            <Table.Th><strong>Type</strong></Table.Th>
                                            <Table.Th><strong>Last Compiled</strong></Table.Th>
                                            <Table.Th><strong>Manual Members</strong></Table.Th>
                                            <Table.Th><strong>Scoped Types</strong></Table.Th>
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {result.data.map((set) => (
                                            <Table.Tr
                                                key={set.SetId}
                                                onClick={async () => {
                                                    setSelectedSet(set);
                                                    try {
                                                        const full = await entityService.getEntityById(set.SetId, 'Set');
                                                        setSelectedSetEntity(full);
                                                        setDetailOpen(true);
                                                    } catch (e) {
                                                        console.error('Failed to fetch set for detail', e);
                                                        setSelectedSetEntity({ Id: set.SetId, DisplayName: set.SetDisplayName });
                                                        setDetailOpen(true);
                                                    }
                                                }}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <Table.Td>
                                                    <Text size="sm" fw={500}>
                                                        {set.SetDisplayName}
                                                    </Text>
                                                    <Text size="xs" c="dimmed">
                                                        {set.SetId}
                                                    </Text>
                                                </Table.Td>
                                                <Table.Td>{getStatusBadge(set)}</Table.Td>
                                                <Table.Td>{getSetTypeBadge(set.SetType)}</Table.Td>
                                                <Table.Td>
                                                    <Text size="sm">
                                                        {set.LastCompiled ? formatDateTime(set.LastCompiled) : 'Never'}
                                                    </Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text size="sm">
                                                        {set.ManualMemberCount}
                                                    </Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text size="sm">
                                                        {set.ScopedEntityTypes?.length || 0}
                                                    </Text>
                                                </Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            </Table.ScrollContainer>
                        </Paper>
                    </ScrollArea>

                    <Divider my="md" />

                    <Text fw={600} size="md" mb="xs">
                        Detailed Information
                    </Text>
                    <Text size="sm" c="dimmed" mb="md">
                        Click on a row above to view detailed compilation information, or expand any section below:
                    </Text>

                    {result.data.map((set) => renderSetDetails(set))}
                </Box>
            )}

            <Group justify="flex-end" mt="md">
                <Button onClick={onClose}>
                    Close
                </Button>
                {result && (
                    <Button
                        onClick={handleRetry}
                        variant="outline"
                        disabled={loading}
                    >
                        Refresh Results
                    </Button>
                )}
            </Group>

            {/* Detail dialog for clicked set */}
            {detailOpen && selectedSetEntity && (
                <EntityDetailDialog
                    open={detailOpen}
                    entity={selectedSetEntity}
                    entityType="Set"
                    onClose={() => { setDetailOpen(false); setSelectedSetEntity(null); }}
                    onDelete={async (entity) => {
                        // Delete operation - let EntityDetailDialog handle errors
                        await entityService.deleteEntity('Set', entity.Id);
                        setDetailOpen(false);
                        setSelectedSetEntity(null);
                        // refresh compilation list after deletion
                        await loadCompilations();
                    }}
                />
            )}
        </Modal>
    );
};
